import React from 'react';
import moment from 'moment';

const SimpleEleganceTemplate = ({ packageData, user }) => {
    if (!packageData?.package) return <div>Loading...</div>;
    const { package: pkg } = packageData;

    // --- Style Palette ---
    const accentColor = '#087EA4'; // A nice teal/blue accent
    const lightBorder = '#eee';
    const lightBackground = '#f9f9f9';
    const secondaryTextColor = '#666';
    const primaryTextColor = '#333';

    // --- Helper Components for consistency ---
    const Section = ({ children, title }) => (
        <div style={{ marginBottom: '30px' }}>
            <h3 style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: '10px', fontSize: '20px', marginBottom: '20px' }}>{title}</h3>
            {children}
        </div>
    );
    
    const SimpleTable = ({ children }) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            {children}
        </table>
    );
    
    const thStyle = { padding: '10px', borderBottom: `2px solid ${lightBorder}`, textAlign: 'left', fontWeight: 'bold' };
    const tdStyle = { padding: '10px', borderBottom: `1px solid ${lightBorder}` };

    return (
        <div style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", color: primaryTextColor, padding: '30px', backgroundColor: '#fff' }}>
            {/* Header */}
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${accentColor}`, paddingBottom: '20px', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>{pkg.packageName}</h1>
                    <p style={{ margin: '5px 0 0', fontSize: '16px', color: secondaryTextColor }}>{pkg.destinationsCover}</p>
                </div>
                {user?.logo && (
                    <img src={user.logo} alt="Company Logo" style={{ maxHeight: '50px', maxWidth: '150px' }} />
                )}
            </header>

            {/* Hero Image */}
            <img src={pkg.imageSrc} alt={pkg.packageName} style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px', marginBottom: '30px' }} />

            {/* Package Summary */}
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', padding: '20px', backgroundColor: lightBackground, borderRadius: '8px', marginBottom: '40px' }}>
                <div>
                    <p style={{ margin: 0, fontSize: '14px', color: secondaryTextColor }}>Duration</p>
                    <p style={{ margin: '5px 0 0', fontSize: '18px', fontWeight: '600' }}>{pkg.duration}</p>
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: '14px', color: secondaryTextColor }}>Start Date</p>
                    <p style={{ margin: '5px 0 0', fontSize: '18px', fontWeight: '600' }}>{moment(pkg.startedDate).format("D MMM, YYYY")}</p>
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: '14px', color: secondaryTextColor }}>Price</p>
                    <p style={{ margin: '5px 0 0', fontSize: '18px', fontWeight: '600', color: accentColor }}>{pkg.price} {pkg.currency}</p>
                </div>
            </div>

            {/* Overview */}
            <Section title="Overview">
                <p style={{ lineHeight: '1.6', fontSize: '15px' }}>{pkg.description}</p>
            </Section>

            {/* Itinerary */}
            {packageData.itineraries?.length > 0 && (
                <Section title="Daily Itinerary">
                    {packageData.itineraries.slice().sort((a, b) => new Date(a.date) - new Date(b.date)).map((item, index) => (
                        <div key={index} style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${lightBorder}` }}>
                            <h5 style={{ fontWeight: 'bold' }}>Day {index + 1}: {item.title} - <span style={{ color: '#555', fontWeight: 'normal' }}>{moment(item.date).format("MMMM Do")}</span></h5>
                            <p style={{ margin: '5px 0 0' }}>{item.description}</p>
                        </div>
                    ))}
                </Section>
            )}
            
            {/* --- FLIGHT DETAILS --- */}
            {packageData.flightBookings?.length > 0 && (
                <Section title="Flight Details">
                    {packageData.flightBookings.map((flight, index) => (
                         <div key={index} style={{ backgroundColor: lightBackground, padding: '15px', borderRadius: '4px', marginBottom: '10px' }}>
                             <h5 style={{ fontWeight: 'bold', margin: '0 0 10px' }}>{flight.departureCity} to {flight.arrivalCity}</h5>
                             <p style={{ margin: '0 0 5px' }}><strong>Airline:</strong> {flight.airline} {flight.flightNumber}</p>
                             <p style={{ margin: 0 }}><strong>Departure:</strong> {moment(flight.departureDateTime).format("MMM D, YYYY at h:mm A")}</p>
                             <p style={{ margin: 0 }}><strong>Arrival:</strong> {moment(flight.arrivalDateTime).format("MMM D, YYYY at h:mm A")}</p>
                         </div>
                    ))}
                </Section>
            )}

            {/* --- ACCOMMODATIONS --- */}
            {packageData.hotelBookings?.length > 0 && (
                <Section title="Accommodations">
                    <SimpleTable>
                        <thead><tr><th style={thStyle}>City</th><th style={thStyle}>Hotel</th><th style={thStyle}>Check-In</th><th style={thStyle}>Check-Out</th><th style={thStyle}>Room Type</th></tr></thead>
                        <tbody>
                            {packageData.hotelBookings.map((hotel, index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{hotel.city}</td>
                                    <td style={tdStyle}>{hotel.hotelName}</td>
                                    <td style={tdStyle}>{moment(hotel.checkInDate).format("DD MMM YYYY")}</td>
                                    <td style={tdStyle}>{moment(hotel.checkOutDate).format("DD MMM YYYY")}</td>
                                    <td style={tdStyle}>{hotel.roomType}</td>
                                </tr>
                            ))}
                        </tbody>
                    </SimpleTable>
                </Section>
            )}

            {/* --- SIGHTSEEING & TRANSFERS --- */}
            {(packageData.sightSeeings?.length > 0 ) && (
                <div style={{ display: 'flex', gap: '30px', marginBottom: '30px' }}>
                    {packageData.sightSeeings?.length > 0 && (
                        <div style={{ flex: 1 }}>
                            <h3 style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: '10px', fontSize: '20px', marginBottom: '15px' }}>Sightseeing</h3>
                             <ul style={{ paddingLeft: '20px', listStyle: 'disc' }}>
                                {packageData.sightSeeings.map((sight, index) => (
                                    <li key={index} style={{ marginBottom: '8px' }}>{sight.sightseeingName} in {sight.destination}
                                        <span style={{ fontStyle: 'italic', color: '#888' }}> - {moment(sight.date).format("MMM D, YYYY")}</span>
                                        {sight.description && <p style={{ margin: '5px 0 15px 20px', fontSize: '14px', color: secondaryTextColor }}>{sight.description}</p>}
                                    </li>
                                    
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            {(packageData.transfers?.length > 0) && (
                <div style={{ flex: 1 }}>
                    <h3 style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: '10px', fontSize: '20px', marginBottom: '15px' }}>Transfers</h3>
                    <ul style={{ paddingLeft: '20px', listStyle: 'disc' }}>
                        {packageData.transfers.map((transfer, index) => (
                                    <li key={index} style={{ marginBottom: '8px' }}>{transfer.fromLocation} to {transfer.toLocation}</li>
                                ))}
                            </ul>
                        </div>
                    )}

            {/* Includes & Excludes */}
            <div style={{ display: 'flex', gap: '40px', marginBottom: '40px' }}>
                {packageData.includes?.length > 0 && (
                    <div style={{ flex: 1 }}>
                        <h3 style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: '10px', fontSize: '20px', marginBottom: '15px' }}>What's Included</h3>
                        <ul style={{ paddingLeft: '20px', margin: 0, listStyle: 'none' }}>
                            {packageData.includes.map((item, index) => (
                                <li key={index} style={{ marginBottom: '8px', position: 'relative' }}>
                                    <span style={{ color: '#28a745', position: 'absolute', left: '-20px' }}>✓</span> {item.description}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {packageData.excludes?.length > 0 && (
                    <div style={{ flex: 1 }}>
                        <h3 style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: '10px', fontSize: '20px', marginBottom: '15px' }}>What's Not Included</h3>
                        <ul style={{ paddingLeft: '20px', margin: 0, listStyle: 'none' }}>
                            {packageData.excludes.map((item, index) => (
                                <li key={index} style={{ marginBottom: '8px', position: 'relative' }}>
                                    <span style={{ color: '#dc3545', position: 'absolute', left: '-20px' }}>✗</span> {item.description}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* --- TERMS & CONDITIONS --- */}
            {packageData.termsandConditions?.length > 0 && (
                <Section title="Terms & Conditions">
                    {packageData.termsandConditions.map((term, index) => (
                        <div key={index} style={{ marginBottom: '15px' }}>
                            <h5 style={{ fontWeight: 'bold' }}>{term.title}</h5>
                            <p style={{ fontSize: '14px', lineHeight: '1.6', color: secondaryTextColor }}>{term.description}</p>
                        </div>
                    ))}
                </Section>
            )}

            {/* Footer */}
            <footer style={{ textAlign: 'center', paddingTop: '20px', borderTop: `1px solid ${lightBorder}`, marginTop: '30px', fontSize: '12px', color: '#888' }}>
                <p>Thank you for choosing us! For questions, contact {pkg.contactEmail} or {pkg.contactPhoneNumber}.</p>
            </footer>
        </div>
    );
};

export default SimpleEleganceTemplate;